import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TokenStyles.css';

const TokenMarketplace = ({ userId, onStrategyPurchase }) => {
  const [strategies, setStrategies] = useState([]);
  const [bounties, setBounties] = useState([]);
  const [userTokens, setUserTokens] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('strategies');
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [contributionForm, setContributionForm] = useState({
    type: 'strategy',
    content: '',
    description: '',
    subject: '',
    domain: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchMarketplaceData();
  }, [userId]);

  const fetchMarketplaceData = async () => {
    setLoading(true);
    try {
      // Parallel API calls for better performance
      const [tokenResponse, strategiesResponse, bountiesResponse] = await Promise.all([
        axios.get(`/api/protocol/v1/incentives/tokens?user_id=${userId}`),
        axios.get('/api/protocol/v1/community/strategies/market'),
        axios.get('/api/protocol/v1/community/bounties')
      ]);

      setUserTokens(tokenResponse.data.balance || 0);
      setStrategies(strategiesResponse.data.strategies || []);
      setBounties(bountiesResponse.data.bounties || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching marketplace data:', err);
      setError('Failed to load marketplace data. Please try again.');
      
      // Set demo data for development/testing
      setUserTokens(120);
      setStrategies([
        {
          id: 'strat1',
          name: 'Visual Learning Algebra',
          description: 'A strategy focused on visual representations of algebraic concepts',
          subject: 'Mathematics',
          domain: 'Algebra',
          creator: 'Prof. Miller',
          price: 25,
          rating: 4.7,
          reviews: 32,
          popularity: 87,
          dateCreated: '2025-02-15T10:30:00Z'
        },
        {
          id: 'strat2',
          name: 'Step-by-Step Problem Solver',
          description: 'Methodical approach to breaking down complex problems',
          subject: 'Mathematics',
          domain: 'Problem Solving',
          creator: 'Dr. Chen',
          price: 15,
          rating: 4.9,
          reviews: 57,
          popularity: 95,
          dateCreated: '2025-01-28T14:45:00Z'
        },
        {
          id: 'strat3',
          name: 'Concept Mapping for Calculus',
          description: 'Visual mapping strategy for understanding calculus relationships',
          subject: 'Mathematics',
          domain: 'Calculus',
          creator: 'MathLearningLab',
          price: 30,
          rating: 4.5,
          reviews: 24,
          popularity: 72,
          dateCreated: '2025-03-10T09:15:00Z'
        }
      ]);
      setBounties([
        {
          id: 'bounty1',
          title: 'Statistics Visualization Strategy',
          description: 'Looking for strategies to help students understand statistical concepts visually',
          requester: 'Statistics Teacher Association',
          reward: 75,
          submissions: 3,
          dueDate: '2025-04-15T23:59:59Z',
          status: 'open'
        },
        {
          id: 'bounty2',
          title: 'Geometry Problem-Solving Framework',
          description: 'Need a comprehensive framework for teaching geometry proof techniques',
          requester: 'EducationInnovators',
          reward: 100,
          submissions: 1,
          dueDate: '2025-04-30T23:59:59Z',
          status: 'open'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseStrategy = async (strategy) => {
    try {
      const response = await axios.post('/api/protocol/v1/marketplace/purchase', {
        user_id: userId,
        strategy_id: strategy.id
      });
      
      // Update user tokens
      setUserTokens(prev => prev - strategy.price);
      
      // Notify parent component about purchase
      if (onStrategyPurchase) {
        onStrategyPurchase(strategy);
      }
      
      // Show purchase success
      setSelectedStrategy({...strategy, purchased: true});
      
      return response.data;
    } catch (error) {
      console.error('Error purchasing strategy:', error);
      throw error;
    }
  };

  const handleSubmitContribution = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post('/api/protocol/v1/incentives/contributions', {
        contributor_id: userId,
        contribution_type: contributionForm.type,
        content: {
          strategy_name: contributionForm.description,
          description: contributionForm.description,
          target_topics: [contributionForm.subject],
          taxonomy_level: 'understand',
          logic: contributionForm.content
        },
        metadata: {
          subject: contributionForm.subject,
          domain: contributionForm.domain
        }
      });
      
      // Reset form and show success message
      setContributionForm({
        type: 'strategy',
        content: '',
        description: '',
        subject: '',
        domain: ''
      });
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
      
      // Fetch updated token balance
      const tokenResponse = await axios.get(`/api/protocol/v1/incentives/tokens?user_id=${userId}`);
      setUserTokens(tokenResponse.data.balance || 0);
      
    } catch (error) {
      console.error('Error submitting contribution:', error);
      setError('Failed to submit contribution. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContributionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="marketplace-loading">Loading marketplace data...</div>;
  }

  return (
    <div className="token-marketplace">
      <div className="marketplace-header">
        <h2>Strategy Marketplace</h2>
        <div className="marketplace-balance">
          <span className="token-icon">🪙</span>
          <span className="balance-amount">{userTokens}</span>
          <span className="balance-label">tokens available</span>
        </div>
      </div>
      
      <div className="marketplace-tabs">
        <button 
          className={`tab-button ${activeTab === 'strategies' ? 'active' : ''}`}
          onClick={() => setActiveTab('strategies')}
        >
          Browse Strategies
        </button>
        <button 
          className={`tab-button ${activeTab === 'bounties' ? 'active' : ''}`}
          onClick={() => setActiveTab('bounties')}
        >
          Community Bounties
        </button>
        <button 
          className={`tab-button ${activeTab === 'contribute' ? 'active' : ''}`}
          onClick={() => setActiveTab('contribute')}
        >
          Contribute
        </button>
      </div>
      
      <div className="marketplace-content">
        {activeTab === 'strategies' && (
          <div className="strategies-grid">
            {strategies.map(strategy => (
              <div 
                key={strategy.id} 
                className={`strategy-card ${selectedStrategy?.id === strategy.id ? 'selected' : ''}`}
                onClick={() => setSelectedStrategy(strategy)}
              >
                <div className="strategy-header">
                  <h3>{strategy.name}</h3>
                  <div className="strategy-price">
                    <span className="token-icon">🪙</span>
                    <span>{strategy.price}</span>
                  </div>
                </div>
                
                <p className="strategy-description">{strategy.description}</p>
                
                <div className="strategy-meta">
                  <span className="strategy-domain">{strategy.domain}</span>
                  <span className="strategy-creator">by {strategy.creator}</span>
                </div>
                
                <div className="strategy-stats">
                  <div className="stat-item">
                    <span className="stat-icon">⭐</span>
                    <span className="stat-value">{strategy.rating}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon">👤</span>
                    <span className="stat-value">{strategy.reviews}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'bounties' && (
          <div className="bounties-list">
            {bounties.map(bounty => (
              <div key={bounty.id} className="bounty-card">
                <div className="bounty-header">
                  <h3>{bounty.title}</h3>
                  <div className="bounty-reward">
                    <span className="token-icon">🪙</span>
                    <span>{bounty.reward}</span>
                  </div>
                </div>
                
                <p className="bounty-description">{bounty.description}</p>
                
                <div className="bounty-meta">
                  <span className="bounty-requester">Requested by: {bounty.requester}</span>
                  <span className="bounty-submissions">Submissions: {bounty.submissions}</span>
                  <span className="bounty-deadline">Due: {new Date(bounty.dueDate).toLocaleDateString()}</span>
                </div>
                
                <button className="bounty-button">
                  Submit Solution
                </button>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'contribute' && (
          <div className="contribution-form-container">
            <h3>Share Your Teaching Strategy</h3>
            <p className="contribution-intro">
              Contribute your effective teaching strategies and earn tokens! 
              High-quality contributions will be added to the marketplace and earn you ongoing rewards.
            </p>
            
            {submitSuccess && (
              <div className="submission-success">
                Thank you for your contribution! It has been submitted for review.
              </div>
            )}
            
            <form className="contribution-form" onSubmit={handleSubmitContribution}>
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="type">Contribution Type</label>
                  <select 
                    id="type" 
                    name="type" 
                    value={contributionForm.type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="strategy">Teaching Strategy</option>
                    <option value="content">Exercise Set</option>
                    <option value="verification">Assessment</option>
                  </select>
                </div>
                
                <div className="form-field">
                  <label htmlFor="subject">Subject Area</label>
                  <select 
                    id="subject" 
                    name="subject" 
                    value={contributionForm.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Science">Science</option>
                    <option value="English">English</option>
                    <option value="History">History</option>
                    <option value="Computer Science">Computer Science</option>
                  </select>
                </div>
                
                <div className="form-field">
                  <label htmlFor="domain">Specific Domain</label>
                  <input 
                    type="text" 
                    id="domain" 
                    name="domain" 
                    value={contributionForm.domain}
                    onChange={handleInputChange}
                    placeholder="e.g., Algebra, Chemistry, etc."
                    required
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label htmlFor="description">Strategy Name & Brief Description</label>
                <input 
                  type="text" 
                  id="description" 
                  name="description" 
                  value={contributionForm.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of your teaching strategy"
                  required
                />
              </div>
              
              <div className="form-field">
                <label htmlFor="content">Strategy Details</label>
                <textarea 
                  id="content" 
                  name="content" 
                  value={contributionForm.content}
                  onChange={handleInputChange}
                  placeholder="Describe your teaching strategy in detail. Include examples of how it should be used and when it's most effective."
                  rows={10}
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="submit-contribution" 
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Contribution'}
              </button>
            </form>
          </div>
        )}
      </div>
      
      {selectedStrategy && activeTab === 'strategies' && (
        <div className="strategy-detail-panel">
          <div className="strategy-detail-header">
            <h3>{selectedStrategy.name}</h3>
            <button className="close-detail" onClick={() => setSelectedStrategy(null)}>×</button>
          </div>
          
          <div className="strategy-detail-content">
            <div className="strategy-main-info">
              <p className="strategy-full-description">{selectedStrategy.description}</p>
              
              <div className="strategy-meta-detail">
                <div className="meta-item">
                  <span className="meta-label">Subject:</span>
                  <span className="meta-value">{selectedStrategy.subject}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Domain:</span>
                  <span className="meta-value">{selectedStrategy.domain}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Creator:</span>
                  <span className="meta-value">{selectedStrategy.creator}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Date Added:</span>
                  <span className="meta-value">{new Date(selectedStrategy.dateCreated).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            
            <div className="strategy-purchase-section">
              <div className="purchase-price">
                <span className="token-icon large">🪙</span>
                <span className="price-amount">{selectedStrategy.price}</span>
                <span className="price-label">tokens</span>
              </div>
              
              {selectedStrategy.purchased ? (
                <div className="already-purchased">
                  <span className="check-icon">✓</span>
                  <span>Already Purchased</span>
                </div>
              ) : (
                <button 
                  className="purchase-button" 
                  onClick={() => handlePurchaseStrategy(selectedStrategy)}
                  disabled={userTokens < selectedStrategy.price}
                >
                  {userTokens < selectedStrategy.price ? 'Not Enough Tokens' : 'Purchase Strategy'}
                </button>
              )}
              
              <div className="purchase-info">
                <div className="info-item">
                  <span className="info-icon">⭐</span>
                  <span className="info-text">Rating: {selectedStrategy.rating}/5</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">👤</span>
                  <span className="info-text">{selectedStrategy.reviews} Reviews</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">📈</span>
                  <span className="info-text">{selectedStrategy.popularity}% Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenMarketplace;